import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Card, CardContent, Button, CircularProgress, Chip } from '@mui/material';
import { HourglassEmpty, Logout } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { InlineMessage } from '../components/ui';

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
};

/**
 * Waiting room for a buying-group / head-office application
 * (`onboardingService.completeGroupOnboarding`). Nothing here grants any
 * access: `canAccessApp` keeps this user off `/app/dashboard` for as long as
 * the group's `organizationGroups` doc is not `active`, so this is the only
 * page they can reach until a Motzu admin approves it (or the only outcome
 * they see if it was rejected).
 *
 * Pre-shell page (no Layout/sidebar), matching its onboarding siblings.
 */
export const OnboardingGroupPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loggingOut, setLoggingOut] = React.useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['onboarding', 'group-pending'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/onboarding/group-pending', { headers });
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to load your application status'));
      }
      return response.json();
    },
    // Approval can land at any time; a short poll saves the applicant a
    // manual refresh without hammering the endpoint.
    refetchInterval: 30_000,
  });

  const group = data?.data?.group;

  React.useEffect(() => {
    // Approved: the group overview is where a group's own dashboard lives
    // (see `onboardingService.getNextStep`) — `/app/dashboard` only knows
    // the store, vendor and admin shells.
    if (group?.status === 'active') {
      navigate('/app/group');
    }
  }, [group?.status, navigate]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out:', err);
      setLoggingOut(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        pt: { xs: 6, md: 10 },
        pb: { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="text"
            startIcon={<Logout />}
            onClick={handleLogout}
            disabled={loggingOut}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={40} />
          </Box>
        ) : isError ? (
          <InlineMessage severity="error">
            {error instanceof Error ? error.message : 'Failed to load your application status'}
          </InlineMessage>
        ) : (
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              <HourglassEmpty sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                {group?.status === 'rejected' ? 'Application not approved' : 'Application under review'}
              </Typography>

              {group?.name && (
                <Chip label={group.name} sx={{ mb: 2, fontWeight: 600 }} />
              )}

              {group?.status === 'rejected' ? (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    A Motzu admin reviewed this application and it was not approved.
                  </Typography>
                  {group.rejectionReason && (
                    <InlineMessage severity="info">{group.rejectionReason}</InlineMessage>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    If you think this is a mistake, reach out to Motzu support.
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  A Motzu admin reviews every buying group and head office before it can manage
                  member stores. We'll let you know once it's approved — no store data or catalog
                  access is granted until then. You can safely close this page and check back
                  later.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default OnboardingGroupPendingPage;
